import React from 'react';
import { useNavigate } from 'react-router-dom';

import { getNewStaffIds, markStaffListSeen, getStaffUpdatesCustomerId } from '../../utils/staffUpdates';
import { getStoredCustomerAuthToken } from '../../utils/auth/customerAuthToken';
import SubpageHeader from './SubpageHeader';
import StaffAccordionCard from './StaffAccordionCard';
import { useReserveContext } from '../../contexts/useReserveContext';
import {
  fetchStoresForStaffReserve,
  type StoreForReserve,
} from '../../utils/reservation/by-staff/fetchStoresForStaffReserve';
import {
  fetchStaffsForStaffReserve,
  type StaffForStaffReserve,
} from '../../utils/reservation/by-staff/fetchStaffsForStaffReserve';
import { pickPreferredStore } from './homeShared';
import { applySelectedStore } from './storeSelection';
import { useI18n } from '../../i18n';

const StaffDirectoryPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useI18n();
  const {
    storeId,
    setSkippedStoreSelectionByStaff,
    setStoreId,
    setStoreName,
    setSelectedServices,
    setSelectedDate,
    setSelectedStartTime,
    setSelectedStaffId,
    setSelectedStaffName,
    setIsFreeReservation,
  } = useReserveContext();
  const [newStaffIds, setNewStaffIds] = React.useState<number[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [staffs, setStaffs] = React.useState<StaffForStaffReserve[]>([]);
  const [expandedStaffId, setExpandedStaffId] = React.useState<number | null>(null);
  const [preferredStore, setPreferredStore] = React.useState<StoreForReserve | null>(null);

  React.useEffect(() => {
    let isMounted = true;
    const token = getStoredCustomerAuthToken();
    const customerId = getStaffUpdatesCustomerId();

    const load = async () => {
      setLoading(true);
      setNewStaffIds([]);
      try {
        const stores = await fetchStoresForStaffReserve();
        const preferredStore = pickPreferredStore(stores, storeId);

        if (!preferredStore) {
          if (!isMounted) return;
          setPreferredStore(null);
          setStaffs([]);
          setExpandedStaffId(null);
          return;
        }

        const rows = await fetchStaffsForStaffReserve(preferredStore.id);

        if (!isMounted) return;
        setPreferredStore(preferredStore);
        setStaffs(rows);
        if (token && token === getStoredCustomerAuthToken()
          && customerId === getStaffUpdatesCustomerId()
          && customerId !== null) {
          const ids = rows.map((staff) => staff.id);
          setNewStaffIds(getNewStaffIds(customerId, preferredStore.id, ids));
          markStaffListSeen(customerId, preferredStore.id, ids);
        }
        setExpandedStaffId((prev) =>
          rows.some((staff) => staff.id === prev) ? prev : null,
        );
      } catch (error) {
        console.error(error);
        if (!isMounted) return;
        setPreferredStore(null);
        setStaffs([]);
        setExpandedStaffId(null);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    void load();

    return () => {
      isMounted = false;
    };
  }, [storeId]);

  const toggleExpandedStaff = (staffId: number) => {
    setExpandedStaffId((prev) => (prev === staffId ? null : staffId));
  };

  const handleReserveWithStaff = React.useCallback((staff: StaffForStaffReserve) => {
    if (!preferredStore) return;

    applySelectedStore(
      {
        setStoreId,
        setStoreName,
        setSelectedServices,
        setSelectedDate,
        setSelectedStartTime,
        setSelectedStaffId,
        setSelectedStaffName,
        setIsFreeReservation,
      },
      preferredStore,
    );
    setSkippedStoreSelectionByStaff(true);
    setSelectedStaffId(staff.id);
    setSelectedStaffName(staff.nickname || t('staff.fallbackName'));
    navigate('/reserve/by-staff/service');
  }, [
    preferredStore,
    setStoreId,
    setStoreName,
    setSelectedServices,
    setSelectedDate,
    setSelectedStartTime,
    setSelectedStaffId,
    setSelectedStaffName,
    setIsFreeReservation,
    setSkippedStoreSelectionByStaff,
    navigate,
    t,
  ]);

  return (
    <div className="space-y-4">
      <SubpageHeader title={t('staff.title')} onBack={() => navigate('/reserve')} />

      {loading ? (
        <div className="rounded-[28px] border border-slate-200 bg-white px-5 py-8 text-center text-sm text-slate-500 shadow-sm">
          {t('staff.loading')}
        </div>
      ) : staffs.length === 0 ? (
        <div className="rounded-[28px] border border-slate-200 bg-white px-5 py-8 text-center text-sm text-slate-500 shadow-sm">
          {t('staff.empty')}
        </div>
      ) : (
        <div className="space-y-3">
          {staffs.map((staff, index) => {
            const isExpanded = expandedStaffId === staff.id;

            return (
              <StaffAccordionCard
                key={staff.id}
                isNew={newStaffIds.includes(staff.id)}
                name={staff.nickname || t('staff.fallbackName')}
                roleName={staff.role_name}
                bio={staff.bio}
                iconUrl={staff.icon_url}
                snsLinks={staff.sns_links}
                isExpanded={isExpanded}
                onToggle={() => toggleExpandedStaff(staff.id)}
                onAction={() => handleReserveWithStaff(staff)}
                actionLabel={t('staff.bookWith', { name: staff.nickname || t('staff.thisStaff') })}
                actionEyebrow={t('staff.bookingEyebrow')}
                toneIndex={index}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

export default StaffDirectoryPage;
